// ============================================================
//  Verwendungsdaten (Drill-down): welche Transaktion hat auf welchem Konto die
//  Prüfung eines Berechtigungswerts ausgelöst, und wann zuletzt.
//  Quelle: public/seed/nutzung/<SID>.json aus scripts/seed-to-app.mjs (nutzung_detail.csv).
//  Wird je System erst geladen, wenn jemand den Drill-down öffnet — die Dateien sind groß.
//  Keine Zählung von Aufrufen: Workload-Statistiken werden verdichtet, eine Häufigkeit wäre
//  eine Behauptung. Was belegt ist: Transaktion, Programm, Wert, letzte Verwendung.
// ============================================================
import { useEffect, useState } from "react";

export interface UsageRow {
  roleId: string;
  obj: string;
  field: string;
  val: string;
  tcode: string;
  report: string;
  lastUsed: string; // ISO-Datum
}

type SystemUsage = Record<string, (string | undefined)[][]>; // "MANDT|BNAME" → Zeilen

const cache = new Map<string, Promise<SystemUsage | null>>();

export function loadUsageSystem(sid: string, base = "seed/nutzung"): Promise<SystemUsage | null> {
  let p = cache.get(sid);
  if (!p) {
    p = fetch(`${base}/${sid}.json`, { cache: "no-cache" })
      .then((r) => (r.ok ? (r.json() as Promise<SystemUsage>) : null))
      .catch(() => null);
    cache.set(sid, p);
  }
  return p;
}

export type UsageStatus = "aus" | "laedt" | "fehlt" | "bereit";

/** Verwendungszeilen eines Kontos, sortiert nach letzter Verwendung (neueste zuerst). */
export function useUsage(sid: string, client: string, username: string, enabled: boolean): { status: UsageStatus; rows: UsageRow[] } {
  const [state, setState] = useState<{ key: string; status: UsageStatus; rows: UsageRow[] }>({ key: "", status: "aus", rows: [] });
  const key = enabled ? `${sid}|${client}|${username}` : "";
  useEffect(() => {
    if (!key) return;
    let alive = true;
    setState({ key, status: "laedt", rows: [] });
    loadUsageSystem(sid).then((m) => {
      if (!alive) return;
      if (!m) { setState({ key, status: "fehlt", rows: [] }); return; }
      const rows = (m[`${client}|${username}`] ?? []).map(([roleId, obj, field, val, tcode, report, lastUsed]) => ({
        roleId: roleId ?? "", obj: obj ?? "", field: field ?? "", val: val ?? "", tcode: tcode ?? "", report: report ?? "", lastUsed: lastUsed ?? "",
      }));
      rows.sort((a, b) => (a.lastUsed < b.lastUsed ? 1 : a.lastUsed > b.lastUsed ? -1 : a.tcode.localeCompare(b.tcode)));
      setState({ key, status: "bereit", rows });
    });
    return () => { alive = false; };
  }, [key, sid, client, username]);
  if (!key) return { status: "aus", rows: [] };
  if (state.key !== key) return { status: "laedt", rows: [] };
  return { status: state.status, rows: state.rows };
}
